/**
 * @typedef {object} KioskApiBaseUrlEnvironment
 * @property {string | undefined} [configuredBaseUrl]
 * @property {"development" | "production"} mode
 */

/** @param {string} hostname */
function isLocalOrUnspecifiedHostname(hostname) {
  const normalized = hostname
    .toLowerCase()
    .replace(/^\[|\]$/g, "")
    .replace(/\.$/, "");
  const mappedIpv4HighWord = /^::ffff:([0-9a-f]{1,4}):[0-9a-f]{1,4}$/.exec(
    normalized,
  )?.[1];
  const ipv4Octets = normalized.split(".");
  const isThisNetworkIpv4 =
    ipv4Octets.length === 4 &&
    ipv4Octets.every((octet) => /^\d{1,3}$/.test(octet)) &&
    Number(ipv4Octets[0]) === 0;
  return (
    normalized === "localhost" ||
    normalized.endsWith(".localhost") ||
    normalized === "::" ||
    normalized === "::1" ||
    isThisNetworkIpv4 ||
    /^127(?:\.\d{1,3}){0,3}$/.test(normalized) ||
    (mappedIpv4HighWord !== undefined &&
      [0, 127].includes(Number.parseInt(mappedIpv4HighWord, 16) >> 8))
  );
}

/**
 * Resolve and validate the one API origin shared by the browser transport and
 * the production CSP builder.
 *
 * @param {KioskApiBaseUrlEnvironment} environment
 * @returns {string}
 */
export function resolveKioskApiBaseUrl({ configuredBaseUrl, mode }) {
  const candidate =
    configuredBaseUrl?.trim() ||
    (mode === "development" ? "http://localhost:3000" : undefined);
  if (candidate === undefined) {
    throw new Error("VITE_PLATFORM_GRAPH_BASE_URL is required in production");
  }

  let url;
  try {
    url = new globalThis.URL(candidate);
  } catch {
    throw new Error("VITE_PLATFORM_GRAPH_BASE_URL must be a valid URL");
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("VITE_PLATFORM_GRAPH_BASE_URL must use HTTP or HTTPS");
  }
  if (
    url.username !== "" ||
    url.password !== "" ||
    url.pathname !== "/" ||
    url.search !== "" ||
    url.hash !== ""
  ) {
    throw new Error(
      "VITE_PLATFORM_GRAPH_BASE_URL must be an origin without credentials or a path",
    );
  }
  if (
    mode === "production" &&
    (url.protocol !== "https:" || isLocalOrUnspecifiedHostname(url.hostname))
  ) {
    throw new Error(
      "VITE_PLATFORM_GRAPH_BASE_URL must be a non-local HTTPS origin in production",
    );
  }
  return url.origin;
}
